<script lang="ts" setup>
import { cloneDeep } from 'lodash-es';
import { ref, onMounted, watch } from 'vue';

defineOptions({
  name: 'Pagination',
});

interface PagerData {
  first?: number;
  previous?: number;
  pages?: any;
  next?: number;
  last?: number;
}

const props = defineProps({
  pager: {
    type: Object,
    default() {
      return {
        // 偏移量
        offset: 0,
        // 数据总条数
        total: 0,
      };
    },
  },

  numLinks: {
    type: Number,
    default: 5,
  },

  limit: {
    type: [Number, String],
    default: 20,
  },
});

const emit = defineEmits(['change']);
const listing = ref<any>({});
const totalPage = ref(0);

const refresh = () => {
  const output: PagerData = {};
  const pager = cloneDeep(props.pager);
  let current = (pager.offset || 0) / Number(props.limit) + 1;
  const total = Math.ceil((pager.total || 0) / Number(props.limit));
  totalPage.value = total;

  if (pager.total == 0) {
    listing.value = {};

    return;
  }

  if (current > total) {
    current = total;
  }

  if (total == 1) {
    listing.value = {};

    return;
  }

  const start = ((current - props.numLinks) > 0) ? current - (props.numLinks - 1) : 1;
  const end = ((current + props.numLinks) < total) ? current + props.numLinks : total;

  // 首页
  if (current > (props.numLinks + 1)) {
    output.first = 1;
  }

  // 上一页
  if (current != 1) {
    output.previous = current - 1;
  }

  output.pages = [];

  // 页码
  for (let loop = start - 1; loop <= end; loop++) {
    if (loop >= 1) {
      if (current == loop) {
      // 当前页
        output.pages.push({ number: '', text: loop });
      }
      else {
        output.pages.push({ number: loop, text: loop });
      }
    }
  }

  // 下一页
  if (current < total) {
    output.next = current + 1;
  }

  // 最后一页
  if ((current + props.numLinks) < total) {
    output.last = total;
  }

  listing.value = output;
}

const onPagerClick = (event: Event) => {
  const target = event.target as HTMLElement;

  if (target.tagName.toLowerCase() !== 'a') {
    return;
  }

  const pageNumber = target.getAttribute('data-page');

  emit('change', (Number(pageNumber) - 1) * Number(props.limit));
}

onMounted(() => {
  refresh();
});

watch(() => props.pager, () => {
  refresh();
});
</script>

<template>
  <nav class="page-pagination" aria-label="Page navigation" v-if="typeof listing.pages === 'object' && listing.pages !== null">
    <ul class="pagination flex items-center justify-center text-14px" @click.prevent="onPagerClick">
      <li v-if="listing.previous >= 0">
        <a class="i-summit-icons:arrow-left block" href="javascript:;" rel="prev" :data-page="listing.previous"></a>
      </li>
      <template v-for="(item, index) in listing.pages">
        <li v-if="item.number !== ''" :key="index" class="ml-9px">
          <a href="javascript:;" class="block w-12px text-center" :data-page="item.number">{{ item.text }}</a>
        </li>
        <li class="active ml-9px color-hex-00479d" :key="`active-${index}`" v-else>
          <span class="block w-12px text-center">{{ item.text }}</span>
        </li>
      </template>
      <li v-if="listing.next >= 0" class="ml-9px">
        <a class="i-summit-icons:arrow-right block" href="javascript:;" rel="next" :data-page="listing.next"></a>
      </li>
      <li class="ml-16px">
        共 {{ totalPage }} 页
      </li>
    </ul>
  </nav>
</template>
